import { useEffect, useRef } from 'react';

/**
 * Sets a CSS custom property --vh that represents 1% of the actual viewport height.
 * This is the standard solution used by production apps (Airbnb, Twitter, etc.)
 * to handle mobile browser viewport issues.
 *
 * IMPORTANT: On mobile, we do NOT update --vh when the keyboard opens/closes.
 * This prevents the page from resizing when the user focuses an input field,
 * which would otherwise cause content to shift and hide text.
 *
 * Usage in CSS: height: calc(var(--vh, 1vh) * 100);
 */
export function useViewportHeight() {
  const initialHeightRef = useRef<number | null>(null);

  useEffect(() => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const setVH = (forceUpdate = false) => {
      const currentHeight = window.innerHeight;

      // On mobile, only update on significant height increases (orientation change)
      // or on first load. Don't update when keyboard opens (height decreases).
      if (isMobile && initialHeightRef.current !== null && !forceUpdate) {
        const heightDiff = initialHeightRef.current - currentHeight;
        // If viewport shrunk significantly (keyboard opened), don't update --vh
        if (heightDiff > 150) {
          return;
        }
      }

      // Update the stored height if it increased (keyboard closed or orientation change)
      if (
        initialHeightRef.current === null ||
        currentHeight >= initialHeightRef.current
      ) {
        initialHeightRef.current = currentHeight;
      }

      // Get the actual viewport height (excluding browser chrome)
      const vh = initialHeightRef.current * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set initial value
    setVH(true);

    // Update on resize - but the function will skip keyboard-induced shrinks on mobile
    const handleResize = () => setVH(false);
    window.addEventListener('resize', handleResize);

    // Also update on orientation change for mobile - force update
    const handleOrientationChange = () => {
      // Reset the initial height on orientation change
      initialHeightRef.current = null;
      // Delay to let the browser finish orientation change
      setTimeout(() => setVH(true), 100);
    };
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);
}
